import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { registerDirtySource } from '@onvice/portal-shell-api';
import { changedKeys as diffKeys, isEqualValue } from './editDiff';
import type { UnsavedChangesModalProps } from './UnsavedChangesModal';

export type EditMode = 'read' | 'edit';

export interface EditSessionOptions<T extends object> {
  /** Eindeutig je Dokument, z. B. `report:<id>` */
  id: string;
  /** Für Rückfragen, z. B. „Bericht" */
  label: string;
  /** Geladener Stand; null, solange geladen wird */
  baseline: T | null;
  /** Standard read (Regel R3). edit nur für Neuanlage und Tiefenlink `?edit=1`. */
  initialMode?: EditMode;
  canEdit?: boolean;
  isEqual?: (a: T, b: T) => boolean;
  /** Änderungen außerhalb der Arbeitskopie, z. B. Fotos in der Warteschlange */
  extraDirty?: boolean;
  /** Liefert die Mängel; leer = in Ordnung */
  validate?: (value: T) => string[];
  /**
   * Schreibt. Liefert den neuen Ausgangsstand, wenn der Server Werte ergänzt; sonst gilt
   * der gespeicherte Arbeitsstand als neuer Ausgangsstand. Wirft bei Fehlern.
   */
  onSave: (value: T, changed: Array<keyof T>) => Promise<T | void>;
  /** Zusätzlich beim Verwerfen, z. B. gehaltene Fotos löschen */
  onDiscard?: () => void;
  /** „Abbrechen" verlässt das Dokument (E2). Fehlt es, führt „Abbrechen" in die Anzeige (E10). */
  onLeave?: () => void;
  /** Standard read (E1) */
  afterSave?: EditMode;
  /** false = nicht beim Verlassen-Schutz anmelden (der Aufrufer schützt selbst) */
  guard?: boolean;
  /**
   * true = „Speichern“ prüft und schreibt auch ohne Änderung. Für Neuanlagen: Der leere
   * Ausgangsstand ist noch kein Dokument — erst das Schreiben legt es an.
   */
  saveWhenClean?: boolean;
}

export interface EditSession<T extends object> {
  mode: EditMode;
  editing: boolean;
  /** Arbeitsstand (im Lesemodus = Ausgangsstand); null, solange geladen wird */
  value: T | null;
  setValue: (next: T | ((prev: T) => T)) => void;
  setField: <K extends keyof T>(key: K, v: T[K]) => void;
  dirty: boolean;
  changedKeys: Array<keyof T>;
  saving: boolean;
  /** Fehler des letzten Speicherns — als Modal anzeigen, dann clearError */
  error: string | null;
  clearError: () => void;
  /** Mängel der letzten Prüfung — als Modal anzeigen, dann clearIssues */
  issues: string[];
  clearIssues: () => void;
  startEdit: () => void;
  /** true = gespeichert bzw. nichts zu speichern */
  save: () => Promise<boolean>;
  /** „Abbrechen": fragt bei Änderungen nach */
  cancel: () => void;
  /** Verwirft ohne Rückfrage und wechselt in den Lesemodus */
  discard: () => void;
  /**
   * Setzt den Ausgangsstand neu. Nach einem Teil-Speichern mit `draft`: Der gespeicherte
   * Teil gilt als erledigt, die übrigen Änderungen im Arbeitsstand bleiben erhalten.
   */
  rebase: (next: T, draft?: T) => void;
  unsavedModalProps: UnsavedChangesModalProps;
}

/**
 * Lese- und Bearbeitungsmodus eines Dokuments mit Arbeitskopie (Regel
 * „Dokument-Navigation" R3–R5). „Ungespeichert" ergibt sich aus dem Vergleich von
 * Ausgangs- und Arbeitsstand — ändern und zurückändern ist keine Änderung.
 */
export function useEditSession<T extends object>(opts: EditSessionOptions<T>): EditSession<T> {
  const { id, label, baseline, initialMode = 'read', canEdit = true, extraDirty = false, afterSave = 'read', guard = true } = opts;
  const isEqual = opts.isEqual ?? isEqualValue;

  const [mode, setMode] = useState<EditMode>(initialMode);
  const [base, setBase] = useState<T | null>(baseline);
  const [draft, setDraft] = useState<T | null>(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);

  const editing = mode === 'edit' && canEdit;
  const changed = useMemo(() => (editing && base && draft ? diffKeys(base, draft) : []), [editing, base, draft]);
  const dirty = editing && (extraDirty || (base != null && draft != null && !isEqual(base, draft)));

  // Für Aufrufe außerhalb des Renderns (Verlassen-Schutz, Knöpfe): immer der letzte Stand.
  const live = useRef({ dirty, base, draft, opts });
  live.current = { dirty, base, draft, opts };

  // Neu geladener Stand wird übernommen, solange nichts geändert ist.
  useEffect(() => {
    if (baseline == null) return;
    setBase((prev) => (prev != null && isEqualValue(prev, baseline) ? prev : baseline));
    if (!live.current.dirty) setDraft(baseline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline]);

  const discard = useCallback(() => {
    // Sofort sauber melden: Direkt danach wird navigiert, der Verlassen-Schutz fragt dann nicht.
    live.current.dirty = false;
    setDraft(live.current.base);
    setMode('read');
    setAsking(false);
    setError(null);
    setIssues([]);
    live.current.opts.onDiscard?.();
  }, []);

  useEffect(() => {
    if (!guard) return;
    return registerDirtySource({ id: `edit:${id}`, label, isDirty: () => live.current.dirty, discard });
  }, [guard, id, label, discard]);

  const startEdit = useCallback(() => { if (canEdit) setMode('edit'); }, [canEdit]);

  const setValue = useCallback((next: T | ((prev: T) => T)) => {
    setDraft((prev) => {
      if (typeof next !== 'function') return next;
      return prev == null ? prev : (next as (p: T) => T)(prev);
    });
  }, []);

  const setField = useCallback(<K extends keyof T>(key: K, v: T[K]) => {
    setDraft((prev) => (prev == null ? prev : { ...prev, [key]: v }));
  }, []);

  const rebase = useCallback((next: T, nextDraft?: T) => {
    setBase(next);
    setDraft(nextDraft ?? next);
    // Für Aufrufer, die direkt danach weiterarbeiten (vor dem nächsten Rendern).
    live.current.base = next;
    live.current.draft = nextDraft ?? next;
    if (nextDraft === undefined) live.current.dirty = false;
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const s = live.current;
    const value = s.draft;
    if (value == null) return false;
    if (!s.dirty && !s.opts.saveWhenClean) { setMode(afterSave); return true; }
    const found = s.opts.validate?.(value) ?? [];
    if (found.length > 0) { setIssues(found); return false; }
    setSaving(true);
    setError(null);
    try {
      const keys = s.base ? diffKeys(s.base, value) : (Object.keys(value) as Array<keyof T>);
      const result = await s.opts.onSave(value, keys);
      rebase(result ?? value);
      setMode(afterSave);
      return true;
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Speichern fehlgeschlagen');
      return false;
    } finally {
      setSaving(false);
    }
  }, [afterSave, rebase]);

  const leave = useCallback(() => {
    discard();
    live.current.opts.onLeave?.();
  }, [discard]);

  const cancel = useCallback(() => {
    if (live.current.dirty) setAsking(true);
    else leave();
  }, [leave]);

  return {
    mode: editing ? 'edit' : 'read',
    editing,
    value: editing ? draft : base,
    setValue,
    setField,
    dirty,
    changedKeys: changed,
    saving,
    error,
    clearError: () => setError(null),
    issues,
    clearIssues: () => setIssues([]),
    startEdit,
    save,
    cancel,
    discard,
    rebase,
    unsavedModalProps: { open: asking, label, onStay: () => setAsking(false), onDiscard: leave },
  };
}
